import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { authClient } from "../auth";
import { loginRequest } from "../authConfig";
import { actionCreators, AppState } from "../store";
import { clearAppStorage, wasSignedOut } from "../utils/storageUtils";

type BootstrapScreen = "twilioError" | "signedOut" | "loading" | "ready";

function readEnvFlag(name: string): string | undefined {
  return process.env?.[name];
}

export function useAppBootstrap() {
  // Redux
  const dispatch = useDispatch();
  const { login } = bindActionCreators(actionCreators, dispatch);
  const jwt = useSelector((s: AppState) => s.token);
  const twilioToken = useSelector((s: AppState) => s.twilio.token);

  // ENV (mock mode)
  const USE_MOCKS = useMemo(
    () => String(readEnvFlag("REACT_APP_USE_FAKE_MSAL") ?? "false") === "true",
    []
  );
  const MOCK_JWT = useMemo(
    () => String(readEnvFlag("REACT_APP_JWT_TOKEN") ?? "mock-jwt"),
    []
  );

  // Local state
  const [twilioError, setTwilioError] = useState<unknown>(null);
  const [loading, setLoading] = useState(!USE_MOCKS);
  const [msalReady, setMsalReady] = useState(USE_MOCKS);
  const [signedOut] = useState<boolean>(() => wasSignedOut());

  // Derived
  const isAuth = USE_MOCKS ? true : authClient.isAuthenticated();
  const isMsalLoading = USE_MOCKS
    ? false
    : loading || (authClient.isAuthenticated() && !jwt);
  const isTwilioLoading = isAuth && !!jwt && !twilioToken;

  useEffect(() => {
    if (USE_MOCKS) return;

    let cancelled = false;

    (async () => {
      try {
        await authClient.handleRedirect();
      } catch (e) {
        console.error("[MSAL] Errore in handleRedirect:", e);
      }

      if (cancelled) return;

      if (!authClient.isAuthenticated() && !signedOut) {
        try {
          await authClient.loginRedirect(loginRequest);
        } catch (e: any) {
          if (e?.errorCode === "interaction_in_progress") return;
          console.error("[MSAL] Errore in loginRedirect (bootstrap):", e);
        }
        return;
      }

      if (!cancelled) setMsalReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [USE_MOCKS, signedOut]);

  useEffect(() => {
    if (!msalReady) return;

    (async () => {
      if (USE_MOCKS) {
        if (!jwt) login(MOCK_JWT);
        setLoading(false);
        return;
      }

      const accounts = authClient.getAccounts();
      try {
        if (!authClient.isAuthenticated()) {
          setLoading(true);
          return;
        }

        if (jwt) {
          setLoading(false);
          return;
        }

        if (!accounts.length) {
          setLoading(false);
          return;
        }

        const { accessToken } = await authClient.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        login(accessToken);
      } catch (e) {
        console.error(e);
        await authClient.logoutRedirect({
          account: accounts[0] as any,
          postLogoutRedirectUri: window.location.origin,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [USE_MOCKS, MOCK_JWT, msalReady, jwt, login]);

  const handleRetryLogin = async () => {
    setTwilioError(null);
    setLoading(true);

    if (USE_MOCKS) {
      clearAppStorage();
      window.location.reload();
      return;
    }

    if (authClient.getInProgress() !== "none") {
      console.warn(
        "[MSAL] interaction_in_progress: login/logout già in corso."
      );
      return;
    }

    clearAppStorage();
    const accounts = authClient.getAccounts();
    const account = accounts[0];

    try {
      if (authClient.isAuthenticated() && account) {
        await authClient.logoutRedirect({
          account: account as any,
          postLogoutRedirectUri: window.location.origin,
        });
      } else {
        await authClient.loginRedirect(loginRequest);
      }
    } catch (e: any) {
      if (e?.errorCode === "interaction_in_progress") return;
      console.error("[MSAL] Errore in handleRetryLogin:", e);
      setTwilioError(e);
      setLoading(false);
    }
  };

  const screen: BootstrapScreen = useMemo(() => {
    if (twilioError) return "twilioError";
    if (!USE_MOCKS && !isAuth && signedOut) return "signedOut";
    if (!msalReady || isMsalLoading || isTwilioLoading) return "loading";
    return "ready";
  }, [
    USE_MOCKS,
    isAuth,
    isMsalLoading,
    isTwilioLoading,
    msalReady,
    signedOut,
    twilioError,
  ]);

  return {
    // state
    jwt,
    twilioToken,
    screen,
    // actions
    handleRetryLogin,

    onTwilioError: setTwilioError,
  };
}
